import { Box, Button, Stack, Typography } from '@mui/material'
import { Fade } from 'utoniq-ui'

import { BaseLayout } from '@/components/layouts'
import { StandAloneThemeProvider, styleSets } from '@/components/styles'
import { StorageImage, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'
import { individualHideTutorialSkip } from '@/core/individual'

export type TutorialStepProps = {
  image: { uri: string }
  title: string
  caption: string
  onNextStep: () => void
  onSkip: () => void
  currentStep: number
  stepLength: number
}

export const TutorialStep: Organism<TutorialStepProps> = ({
  image,
  title,
  caption,
  onNextStep,
  onSkip,
  currentStep,
  stepLength,
}) => {
  const { t } = useTranslation('common')

  return (
    <StandAloneThemeProvider>
      <BaseLayout>
        <OrganismFrame sx={{ position: 'relative' }}>
          {!individualHideTutorialSkip() && (
            <Box sx={{ position: 'absolute', top: 10, right: 8 }}>
              <Button variant="text" onClick={onSkip}>
                <Typography variant="h6">{t('tutorialSkip')}</Typography>
              </Button>
            </Box>
          )}
          <Stack sx={{ width: '100%', height: '100vh' }}>
            <Fade
              style={{
                height: 'calc(100vw * 1170 / 1125)',
                maxHeight: 'calc(600px * 1170 / 1125)',
              }}
            >
              <StorageImage
                src={image.uri}
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
              />
            </Fade>
            <Stack sx={{ px: 7, ...styleSets.extraExtraSmallMedia({ px: 4 }) }}>
              <Typography variant="h2" sx={{ mb: 4 }} align="center">
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {caption}
              </Typography>
              <Stack alignItems="center">
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ py: 8, ...styleSets.extraExtraSmallMedia({ py: 6 }) }}
                >
                  {Array.from(Array(stepLength).keys()).map(index => (
                    <Box
                      key={index}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor:
                          currentStep === index ? 'primary.main' : 'grey.200',
                      }}
                    />
                  ))}
                </Stack>
                <Button
                  size="large"
                  sx={{ minWidth: 244, mb: 4 }}
                  onClick={onNextStep}
                >
                  {currentStep === stepLength - 1
                    ? t('tutorialStart')
                    : t('tutorialNext')}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </OrganismFrame>
      </BaseLayout>
    </StandAloneThemeProvider>
  )
}
